/**
 * useGlobalKeys (B10) — the app's one global Escape (§H.1, §A.4).
 *
 *   menu → layers card → drive → card → sheet (mobile full → half) → search text
 *
 * The drive, card, sheet and search layers are built in (store actions by
 * default; the bridge can pass its own). Menus and the layers card register with
 * useEscapeLayer(id, ESCAPE_PRIORITY.x, open, close) so Escape closes them even
 * when focus is elsewhere. A component that handles Escape itself while focused
 * (search field, sort menu, native <dialog>) calls event.preventDefault() and
 * this handler stands aside. Never active in kiosk attract (touch wakes it) or
 * while a modal dialog is open.
 *
 * Safe to call more than once: one window listener, the latest options win.
 */
import { useEffect, useRef } from "react";
import { actions, getApp } from "@/store/store";
import { closeTopLayer, ESCAPE_PRIORITY, registerEscapeLayer } from "./escape";

export interface GlobalKeysOptions {
  /** false turns the handler off for this caller. */
  enabled?: boolean;
  /** Escape during a drive. Default actions.stopDrive() (the bridge ends the drive "as on arrive", without rings). */
  onStopDrive?: () => void;
  /** Escape with a card open. Default actions.select(null, "key"). */
  onCloseCard?: () => void;
}

/** Mounted callers, in mount order; each holds a live ref to its latest options. */
const callers = new Map<symbol, { current: GlobalKeysOptions }>();
let detach: (() => void) | null = null;

function options(): GlobalKeysOptions {
  let last: GlobalKeysOptions = {};
  callers.forEach((o) => {
    last = o.current;
  });
  return last;
}

function modalOpen(): boolean {
  const d = document.querySelector("dialog[open]");
  if (!d) return false;
  try {
    return d.matches(":modal");
  } catch {
    return true;
  }
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
  const o = options();
  if (o.enabled === false) return;
  if (getApp().phase === "attract" || modalOpen()) return;
  if (closeTopLayer()) e.preventDefault();
}

function install(): () => void {
  const offs = [
    registerEscapeLayer({
      id: "drive",
      priority: ESCAPE_PRIORITY.drive,
      isOpen: () => getApp().phase === "drive",
      close: () => (options().onStopDrive ?? actions.stopDrive)(),
    }),
    registerEscapeLayer({
      id: "card",
      priority: ESCAPE_PRIORITY.card,
      isOpen: () => getApp().selectedId !== null,
      close: () => (options().onCloseCard ?? (() => actions.select(null, "key")))(),
    }),
    registerEscapeLayer({
      id: "sheet",
      priority: ESCAPE_PRIORITY.sheet,
      isOpen: () => {
        const s = getApp();
        return s.layout === "mobile" && s.sheet === "full";
      },
      close: () => actions.setSheet("half"),
    }),
    registerEscapeLayer({
      id: "search",
      priority: ESCAPE_PRIORITY.search,
      isOpen: () => getApp().query !== "",
      close: () => actions.setQuery(""),
    }),
  ];
  window.addEventListener("keydown", onKeyDown);
  return () => {
    window.removeEventListener("keydown", onKeyDown);
    offs.forEach((off) => off());
  };
}

export function useGlobalKeys(opts: GlobalKeysOptions = {}): void {
  const id = useRef<symbol | null>(null);
  if (id.current === null) id.current = Symbol("global-keys");
  const me = id.current;
  // Latest options without re-subscribing.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    callers.set(me, optsRef);
    if (!detach) detach = install();
    return () => {
      callers.delete(me);
      if (!callers.size && detach) {
        detach();
        detach = null;
      }
    };
  }, [me]);
}

/**
 * Registers a closable layer for the global Escape while `open` is true.
 * Example (B9): useEscapeLayer("layers", ESCAPE_PRIORITY.layers, layersOpen, () => setLayersOpen(false)).
 */
export function useEscapeLayer(id: string, priority: number, open: boolean, close: () => void): void {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return undefined;
    return registerEscapeLayer({ id, priority, isOpen: () => true, close: () => closeRef.current() });
  }, [id, priority, open]);
}

export { ESCAPE_PRIORITY } from "./escape";
